import type {
    AdIntelligenceRecordsAdvertiserDailyOverviewMetric,
    AdIntelligenceRecordsSummaryRecord,
} from "@similarweb/pro-api-client";

import type { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type { AdIntelligenceController } from "../shared/types/types";

export enum PlacementKeys {
    ABOVE_THE_FOLD = "1",
    BELOW_THE_FOLD = "2",
    FAR_BELOW_THE_FOLD = "3+",
}

interface IScraperProps {
    isFromNewScraper?: boolean;
    startDate?: string;
}

export type PerformanceMetric = AdIntelligenceRecordsAdvertiserDailyOverviewMetric;

type WithVisits<T = PerformanceMetric> = { visits: T; pageViews?: never };
type WithPageViews<T = PerformanceMetric> = { pageViews: T; visits?: never };

export type PerformanceResponse = (WithVisits | WithPageViews) & {
    impressions: PerformanceMetric;
    spend: PerformanceMetric;
    placements?: { above: number; below: number; farBelow: number };
    hasAdIntelMetrics?: boolean;
    startDate?: string;
};

type PerformanceDataBase<T> = {
    spend: T;
    impressions: T;
} & (WithVisits<T> | WithPageViews<T>);

export type PerformanceDataPoint = PerformanceDataBase<PerformanceMetric>;
export type PerformanceTotalMetrics = PerformanceDataBase<number>;

export type GranularityKey = Lowercase<GranularityNames>;
export type DeviceKey = Exclude<Lowercase<DEVICES_TYPES_FOR_API>, "both">;

export type DevicePerformanceData = {
    [K in GranularityKey]?: Record<string, PerformanceDataPoint>;
} & {
    total: PerformanceTotalMetrics;
};

export type DomainPerformanceData = Record<DeviceKey, DevicePerformanceData>;

export interface IPerformanceOverTimeResponse {
    data: Record<string, DomainPerformanceData>;
    hasAdIntelMetrics: boolean;
}

export type EntitiesMetric = AdIntelligenceRecordsSummaryRecord;

interface ICommonEntitiesResponse extends IScraperProps {
    campaigns: EntitiesMetric;
    creatives: EntitiesMetric;
}

interface IAdvertiserResponse extends ICommonEntitiesResponse {
    advertisers: EntitiesMetric;
}

interface IPublisherResponse extends ICommonEntitiesResponse {
    publishers: EntitiesMetric;
}

export type EntitiesResponse<T extends AdIntelligenceController> = T extends "advertiser"
    ? { data: { [website: string]: IAdvertiserResponse }; startDate: string }
    : { data: { [website: string]: IPublisherResponse }; startDate: string };
